// ── OppTSByMonthPage — Term Sheets Signed pivoted by month + opportunity detail ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { PivotMatrix, parseDate, ymIdx, monthEndLabel, isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

const commit = (x) => parseFloat(x.commitment_amount || 0);
const initf = (x) => parseFloat(x.initial_funding_amount || 0);
const eom = (v) => { const d = parseDate(v); return d ? monthEndLabel(ymIdx(d)) : "—"; };

export const OppTSByMonthPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);
  const [stageSel, setStageSel] = useState("");
  const [empSel, setEmpSel] = useState("");

  const load = () => {
    setLoading(true);
    api.get("getOpportunities").then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set(opps.map(o => o.ownerName).filter(Boolean))].sort(), [opps]);
  const sources = useMemo(() => [...new Set(opps.map(o => o.lead_source).filter(Boolean))].sort(), [opps]);
  const stages = useMemo(() => [...new Set(opps.map(o => o.stage).filter(Boolean))].sort(), [opps]);

  const fromD = created.from ? new Date(created.from) : null;
  const toD = created.to ? new Date(created.to) : null;
  const inCreated = (v) => { if (!fromD && !toD) return true; const d = parseDate(v); if (!d) return false; if (fromD && d < fromD) return false; if (toD && d > toD) return false; return true; };

  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const oppsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return opps.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (!inCreated(o.createdat)) return false;
      return true;
    });
  }, [opps, company, sourceSel, ownerSel, stageSel, created]);

  const oppsSel = useMemo(() => empSel ? oppsF.filter(o => (o.ownerName || "—") === empSel) : oppsF, [oppsF, empSel]);
  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(oppsSel, 'companyname', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const signedAll = oppsF.filter(o => o.term_sheet_signed_date);   // pivot (all employees)
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const pick = (k) => setEmpSel(prev => prev === k ? "" : k);

  return <>
    <SectionHeader title="Opp TS by Month" />

    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
        <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
        <select value={stageSel} onChange={e => setStageSel(e.target.value)} style={selStyle}><option value="">All Stages</option>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
        {empSel && <button onClick={() => setEmpSel("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{empSel} <span style={{ fontWeight: 700 }}>✕</span></button>}
      </Card>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
          Opp Created
          <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
          <span>–</span>
          <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
        </label>
      </Card>
    </div>

    <PivotMatrix title="Term Sheets Signed (by signed month)" items={signedAll} rowKeyOf={o => o.ownerName} dateOf={o => o.term_sheet_signed_date} onRowClick={pick} activeRow={empSel} />

    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span>Opportunity Detail</span>
        <ExportBtn onClick={() => exportCSV(detailSorted, ["ownerName", "stage", "companyname", "termination_reason", "lead_source", "account_industry", "account_state", "term_sheet_signed_date", "commitment_amount", "initial_funding_amount"], "opp_ts_by_month.csv")}>Export CSV</ExportBtn>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <STh label="SF Stage"     sortKey="stage"                  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Company Name" sortKey="companyname"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Termination Reason" sortKey="termination_reason" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lead Source"  sortKey="lead_source"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Industry"     sortKey="account_industry"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="State"        sortKey="account_state"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="TS Signed EOM" sortKey="term_sheet_signed_date" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Commitment Amt" sortKey="commitment_amount"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Initial Funding Amt" sortKey="initial_funding_amount" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        </tr></thead>
        <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={9} style={{ color: "var(--color-text-secondary)" }}>No opportunities.</Td></tr> : detailSorted.map(o => (
          <tr key={o.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td>{o.stage || "—"}</Td>
            <Td style={{ fontWeight: 500 }}>{o.companyname || o.name || "—"}</Td>
            <Td>{o.termination_reason || "—"}</Td>
            <Td>{o.lead_source || "—"}</Td>
            <Td>{o.account_industry || "—"}</Td>
            <Td>{o.account_state || "—"}</Td>
            <Td>{o.term_sheet_signed_date ? eom(o.term_sheet_signed_date) : "—"}</Td>
            <TdNum>{fmt(commit(o))}</TdNum>
            <TdNum>{fmt(initf(o))}</TdNum>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={7} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + commit(o), 0))}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + initf(o), 0))}</td>
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
